import React, {useState} from "react"
import {useNavigate} from "react-router-dom"
import {Box, Button as ButtonMui, DialogContent, TextField} from "@mui/material"
import {general as textsGeneral} from "../../../translate/Translate.jsx"

import {
    capitalizeFirstLetter,
    cleanTextToNumbers,
    isCNPJ,
    isCPF,
    maskCPF_CNPJ,
    maskInternationalPhone,
    randomNumber
} from "react-lf-tools"
import Button from "../../button/Button.jsx"
import {getClient, postClient} from "../../../services/Apis.jsx"
import {clearLocal, clearSession, getLocal, setLocal} from "../../../hooks/useStorage/useStorage.jsx"
import {LogoutRounded} from '@mui/icons-material'
import {DialogConfirmation} from '../Dialogs.jsx'

import './registerProfile.scss'

export default function RegisterProfile({onClick, onMessage}) {

    const navigate = useNavigate()
    const _profile = getLocal('profile')
    const editing = !!_profile?.key

    const [dialogConfirmation, setDialogConfirmation] = useState({
        open: false,
        title: '',
        message: '',
        type: ''
    })

    const [profile, setProfile] = useState({
        name: _profile?.name || '',
        cpf: _profile?.cpf || '',
        phone: _profile?.phone || '',
        dateOfBirth: _profile?.dateOfBirth || '',
        email: _profile?.email || '',
        password: _profile?.password || ''
    })

    const [repeatPassword, setRepeatPassword] = useState(_profile?.password || '')
    const [errors, setErrors] = useState({})

    const handleChange = (event) => {
        const {name, value} = event.target
        let newValue = value

        const formatters = {
            name: (val) => capitalizeFirstLetter(val),
            phone: (val) => val ? maskInternationalPhone(val) : '',
            cpf: (val) => maskCPF_CNPJ(val.substring(0, 18)),
            email: (val) => val.toLowerCase().trim(),
            password: (val) => val.trim(),
            dateOfBirth: (val) => val
        }

        if (name === "repeatPassword") {
            setRepeatPassword(value)
        } else if (formatters[name]) {
            newValue = formatters[name](value)
            setProfile(prev => ({...prev, [name]: newValue}))
        }
    }

    const validateProfile = (profile, repeatPassword) => {
        const errors = {}

        if (!profile.name || profile.name.trim().length < 3) {
            errors.name = 'Nome inválido'
        }

        if (!profile.phone || profile.phone.replace(/\D/g, '').length < 10) {
            errors.phone = 'Telefone inválido'
        }

        const numericCPF = profile.cpf.replace(/\D/g, '')

        if (!numericCPF || numericCPF.length < 11) {
            errors.cpf = 'CPF ou CNPJ inválido'
        } else {
            if (numericCPF.length > 11) {
                if (!isCNPJ(numericCPF)) errors.cpf = 'CNPJ inválido'
            } else {
                if (!isCPF(numericCPF)) errors.cpf = 'CPF inválido'
            }
        }

        if (!profile.dateOfBirth) {
            errors.dateOfBirth = 'Data de nascimento obrigatória'
        }

        if (!profile.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
            errors.email = 'Email inválido'
        }

        if (!profile.password || profile.password.length < 6) {
            errors.password = 'A senha deve ter pelo menos 6 caracteres'
        }

        if (profile.password !== repeatPassword) {
            errors.repeatPassword = 'As senhas não coincidem'
        }

        return errors
    }

    const handleClickLogout = () => {
        clearLocal()
        clearSession()
        window.location.reload()
    }

    const handleClickConfirmation = () => {
        setDialogConfirmation({...dialogConfirmation, open: false})
        navigate('/login')
    }

    const handleClick = async () => {
        const validationErrors = validateProfile(profile, repeatPassword)
        setErrors(validationErrors)
        if (Object.keys(validationErrors).length === 0) {
            if (!editing) {
                const responseClient = await getClient(profile.email)
                if (responseClient?.returnCode === 1 && !!responseClient?.data) return setDialogConfirmation({
                    open: true,
                    title: textsGeneral.warning,
                    message: textsGeneral.existingRegistration
                })
            }
            const key = _profile?.key || randomNumber()
            const json = {
                nome: profile.name,
                cpf: cleanTextToNumbers(profile.cpf),
                email: profile.email,
                nascimento: profile.dateOfBirth,
                telefone: cleanTextToNumbers(profile.phone),
                ddd: '',
                senha: profile.password,
                key: key
            }
            const request = {json, key}
            const response = await postClient(request)
            if (!response.returnCode) return onMessage(response.message)
            const p = {
                name: profile.name,
                phone: cleanTextToNumbers(profile.phone),
                cpf: cleanTextToNumbers(profile.cpf),
                email: profile.email,
                dateOfBirth: profile.dateOfBirth,
                password: profile.password,
                loggedIn: true,
                key: key
            }
            setLocal('profile', p)
            onClick()
        }
    }

    return <>
        <DialogContent style={{padding: 0}}>
            <div className="register">
                <TextField
                    name="name"
                    value={profile.name}
                    fullWidth
                    autoFocus
                    InputLabelProps={{shrink: true}}
                    color="secondary"
                    label={textsGeneral.name}
                    placeholder={textsGeneral.name}
                    margin="dense"
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                />
                <TextField
                    name="cpf"
                    value={profile.cpf}
                    fullWidth
                    InputLabelProps={{shrink: true}}
                    color="secondary"
                    label={textsGeneral.cpf}
                    placeholder={textsGeneral.cpf}
                    margin="dense"
                    onChange={handleChange}
                    error={!!errors.cpf}
                    helperText={errors.cpf}
                />
                <Box className="box-row">
                    <TextField
                        name="phone"
                        value={profile.phone}
                        fullWidth
                        InputLabelProps={{shrink: true}}
                        color="secondary"
                        label={textsGeneral.phone}
                        placeholder={textsGeneral.phone}
                        margin="dense"
                        onChange={handleChange}
                        error={!!errors.phone}
                        helperText={errors.phone}
                    />
                    <Box p={1}/>
                    <TextField
                        name="dateOfBirth"
                        value={profile.dateOfBirth}
                        fullWidth
                        InputLabelProps={{shrink: true}}
                        color="secondary"
                        label={textsGeneral.dateOfBirth}
                        margin="dense"
                        type="date"
                        onChange={handleChange}
                        error={!!errors.dateOfBirth}
                        helperText={errors.dateOfBirth}
                    />
                </Box>
                <TextField
                    name="email"
                    value={profile.email}
                    fullWidth
                    InputLabelProps={{shrink: true}}
                    color="secondary"
                    margin="dense"
                    label={textsGeneral.email}
                    placeholder={textsGeneral.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                />
                {(!_profile.password) && <Box className="box-row">
                    <TextField
                        name="password"
                        value={profile.password}
                        fullWidth
                        InputLabelProps={{shrink: true}}
                        color="secondary"
                        label={textsGeneral.password}
                        placeholder={textsGeneral.password}
                        margin="dense"
                        type="password"
                        onChange={handleChange}
                        error={!!errors.password}
                        helperText={errors.password}
                    />
                    <Box p={1}/>
                    <TextField
                        name="repeatPassword"
                        value={repeatPassword}
                        fullWidth
                        InputLabelProps={{shrink: true}}
                        color="secondary"
                        label={textsGeneral.repeatPassword}
                        placeholder={textsGeneral.repeatPassword}
                        margin="dense"
                        type="password"
                        onChange={handleChange}
                        error={!!errors.repeatPassword}
                        helperText={errors.repeatPassword}
                    />
                </Box>}
            </div>
        </DialogContent>
        <div className="div-button-register">
            <Button
                style={{marginTop: 10, marginBottom: 10}}
                className={'button-dialog'}
                value={'confirm'}
                title={textsGeneral.confirm}
                onClick={handleClick}
            />
            {(!!_profile?.key) && <ButtonMui
                color="secondary"
                style={{marginTop: 5}}
                endIcon={<LogoutRounded color="secondary"/>}
                onClick={handleClickLogout}>
                Logout
            </ButtonMui>
            }
        </div>
        <DialogConfirmation
            open={dialogConfirmation.open}
            title={dialogConfirmation.title}
            message={dialogConfirmation.message}
            ok={true}
            onClick={handleClickConfirmation}
        />
    </>
}
